import React, { useState, useEffect, useCallback } from "react";
import apiClient from "../api/client";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import "./SyncModal.css";

const FriendProfileModal = ({ userId, onClose }) => {
  const [friendData, setFriendData] = useState(null);
  const [personalCard, setPersonalCard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [cardText, setCardText] = useState("");
  const [cardColor, setCardColor] = useState("#ffffff");
  const [loading, setLoading] = useState(true);
  const { convertDistance, getUnitLabel } = useDistanceUnit();

  const fetchFriendProfile = useCallback(async () => {
    try {
      const response = await apiClient.get(`/friends/profile/${userId}`);

      setFriendData(response.data.friend_stats);
      setPersonalCard(response.data.personal_card);

      if (response.data.personal_card) {
        setCardText(response.data.personal_card.card_text || "");
        setCardColor(response.data.personal_card.card_color || "#ffffff");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching friend profile:", error);
      if (error.response?.status === 403) {
        alert("You don't have permission to view this profile");
        onClose();
      }
      setLoading(false);
    }
  }, [userId, onClose]);

  useEffect(() => {
    fetchFriendProfile();
  }, [fetchFriendProfile]);

  const savePersonalCard = async () => {
    try {
      await apiClient.post(`/friends/card/${userId}`, {
        card_text: cardText,
        card_color: cardColor
      });

      alert("Personal card saved!");
      setIsEditing(false);
      fetchFriendProfile();
    } catch (error) {
      console.error("Error saving personal card:", error);
      alert("Failed to save personal card");
    }
  };

  const convertMilesToUnit = (miles) => {
    return Math.round(convertDistance(miles * 1609.34));
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Loading...</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
        </div>
      </div>
    );
  }

  if (!friendData) {
    return null;
  }

  const profileName = [friendData.first_name, friendData.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() || friendData.display_name || friendData.username || "Runner";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {friendData.profile_picture && (
              <img
                src={friendData.profile_picture}
                alt={profileName}
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid #e5e7eb'
                }}
              />
            )}
            <div>
              <h2 style={{ margin: '0 0 8px 0' }}>
                {profileName}
                {friendData.is_current_user && (
                  <span style={{
                    background: '#14b8a6',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    marginLeft: '12px'
                  }}>
                    This is you!
                  </span>
                )}
              </h2>
              {/* Profile Information inline */}
              {(friendData.first_name || friendData.last_name || friendData.country) && (
                <div style={{ color: '#6b7280', fontSize: '14px' }}>
                  {(friendData.first_name || friendData.last_name) && (
                    <span style={{ marginRight: '12px' }}>
                      {[friendData.first_name, friendData.last_name].filter(Boolean).join(' ')}
                    </span>
                  )}
                  {friendData.country && (
                    <span>{friendData.country}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Stats Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              padding: '28px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px', color: '#0a0a0a' }}>
                {friendData.current_streak}
              </div>
              <div style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>Current Streak</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', marginTop: '4px' }}>
                {friendData.current_streak === 1 ? 'day' : 'days'} {friendData.current_streak > 0 && '🔥'}
              </div>
            </div>

            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              padding: '28px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px', color: '#0a0a0a' }}>
                {friendData.longest_streak}
              </div>
              <div style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>Longest Streak</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', marginTop: '4px' }}>
                {friendData.longest_streak === 1 ? 'day' : 'days'} 🏆
              </div>
            </div>

            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              padding: '28px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px', color: '#0a0a0a' }}>
                {friendData.total_run_days}
              </div>
              <div style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>Total Run Days</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', marginTop: '4px' }}>days</div>
            </div>

            <div style={{
              background: 'white',
              border: '2px solid #e5e7eb',
              padding: '28px',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '8px', color: '#0a0a0a' }}>
                {convertMilesToUnit(friendData.total_distance_miles)}
              </div>
              <div style={{ fontSize: '16px', color: '#6b7280', fontWeight: '500' }}>Total Distance</div>
              <div style={{ fontSize: '14px', color: '#9ca3af', marginTop: '4px' }}>{getUnitLabel()}</div>
            </div>
          </div>

          {/* Personal Note Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>Personal Note</h3>
              {friendData.is_current_user && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  style={{
                    padding: '6px 12px',
                    background: isEditing ? '#6b7280' : '#14b8a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {isEditing ? 'Cancel' : 'Edit Note'}
                </button>
              )}
            </div>

            {isEditing ? (
              <div>
                <div style={{ marginBottom: '16px' }}>
                  <textarea
                    value={cardText}
                    onChange={(e) => setCardText(e.target.value)}
                    placeholder="Add a personal note about yourself..."
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '2px solid #d1d5db',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  onClick={savePersonalCard}
                  style={{
                    padding: '10px 20px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Save Note
                </button>
              </div>
            ) : (
              <div
                style={{
                  padding: '16px',
                  borderRadius: '8px',
                  minHeight: '60px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                {personalCard?.card_text ? (
                  <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#374151' }}>{personalCard.card_text}</p>
                ) : (
                  <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
                    {friendData.is_current_user
                      ? 'No note added yet. Click "Edit Note" to add one.'
                      : 'No note available for this friend.'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendProfileModal;
